// welcome-popup-listener.js
//
// Three jobs, in file order:
//   1. Detect which Alia popup a visitor saw and assign them to the matching
//      Intelligems test group (ALIA_POPUP_VARIATIONS is the popup->group map).
//   2. Keep offer / opt-in state in sync across subdomains via shared cookies
//      (welcome_offer_v4, al_form_optin) so try.lookoptic.com and www agree.
//   3. Render the www sitewide offer experience (header banner, announcement
//      swap, PLP tags, cart messages, PDP notice, slashed pricing) —
//      executeExclusiveOffer / EXCLUSIVE_OFFERS at the bottom of the file.
//
// Runs on www.lookoptic.com (theme.liquid) AND on the try.lookoptic.com
// landing pages (loaded from this theme's CDN via the LP server's
// /theme-assets proxy), so everything here must tolerate both DOMs.

// The try subdomain loads this file without the theme's translation bridge
// (snippets/look-translations.liquid), so window.lookT may not exist at
// parse time. Fall back to the English offer copy there; www always has
// the real bridge in <head> before this script.
if (!window.lookT) {
  const offerFallbacks = {
    'look.offer.close_banner': 'Close offer banner',
    'look.offer.code_label': 'Code:',
    'look.offer.congrats_saved': 'CONGRATS, YOU SAVED [percent]%',
    'look.offer.congrats_saved_up_to': 'CONGRATS, YOU SAVED UP TO [percent]%',
    'look.offer.exclusive_offer': 'Exclusive Offer:',
    'look.offer.free_shipping_over_html': 'FREE SHIPPING ON ORDERS OVER <span data-shipping-threshold>$130</span>',
    'look.offer.free_shipping_prefix': 'FREE SHIPPING ON ORDERS',
    'look.offer.bf_early_access_announcement': 'BLACK FRIDAY EARLY ACCESS | UP TO 35% OFF',
    'look.offer.bf_early_access_banner_html': '<b>BLACK FRIDAY EARLY ACCESS:</b> UP TO 35% OFF SITEWIDE',
    'look.offer.bf_early_access_banner_mobile_html': '<span class="lo-offer-banner__rotate"><b>BLACK FRIDAY EARLY ACCESS</b><span>UP TO 35% OFF SITEWIDE</span></span>',
    'look.offer.off': '[percent]% OFF',
    'look.offer.off_sitewide': '[percent]% OFF SITEWIDE',
    'look.offer.off_sitewide_code': '[percent]% OFF SITEWIDE WITH CODE [code]',
    'look.offer.save_sitewide': 'Save [percent]% sitewide',
    'look.offer.save_up_to': 'Save up to [percent]% sitewide',
    'look.offer.see_details': 'see offer details',
    'look.offer.up_to_amount': 'up to [percent]%',
    'look.offer.up_to_off': 'UP TO [percent]% OFF',
    'look.offer.up_to_off_sitewide': 'UP TO [percent]% OFF SITEWIDE',
    'look.offer.up_to_sitewide_msg': 'Up to [percent]% sitewide',
    'look.offer.use_code': 'Use Code:',
  };
  window.lookT = function (key, vars) {
    let str = offerFallbacks[key] || key;
    if (vars) {
      Object.keys(vars).forEach((name) => {
        str = str.split('[' + name + ']').join(vars[name]);
      });
    }
    return str;
  };
}

// Cross-subdomain cookie helpers (domain=.lookoptic.com), defined once and
// shared across the LOOK OPTIC theme scripts so state written on
// www.lookoptic.com is visible on try.lookoptic.com and vice-versa.
(function () {
  if (window.__sharedCookieHelpersInstalled) return;
  window.__sharedCookieHelpersInstalled = true;

  window.setSharedCookie = function (name, value, days) {
    document.cookie =
      name + "=" + encodeURIComponent(value) +
      "; domain=.lookoptic.com; path=/; SameSite=Lax; Secure" +
      "; max-age=" + (days * 86400);
  };

  window.getSharedCookie = function (name) {
    var cookies = document.cookie ? document.cookie.split("; ") : [];
    for (var i = 0; i < cookies.length; i++) {
      var idx = cookies[i].indexOf("=");
      var key = idx > -1 ? cookies[i].slice(0, idx) : cookies[i];
      if (key === name) {
        return decodeURIComponent(idx > -1 ? cookies[i].slice(idx + 1) : "");
      }
    }
    return null;
  };

  window.removeSharedCookie = function (name) {
    document.cookie = name + "=; domain=.lookoptic.com; path=/; max-age=0";
  };
})();

// Other theme scripts (facets.js, virtual-mirror) still push into this legacy
// FigPii event array; nothing consumes it, but it must exist so they don't throw.
window._fpEvent = window._fpEvent || [];

// Alia popup/flow ids crossed from 5 to 6 digits (e.g. 308873), so the digit
// count must stay open-ended or new popups silently never match.
function waitForSessionStorageKey(key = '^alia-session-\\d{5,}$', timeout = 10000, interval = 50) {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    function findSessionStorageKeys(pattern) {
      const regex = new RegExp(pattern);
      return Object.keys(sessionStorage).filter(key => regex.test(key));
    }

    const checkKey = () => {
      const valueKey = findSessionStorageKeys(key);
      if (valueKey.length !== 0 && window.sessionStorage.getItem(valueKey[0]) !== null) {
        resolve(valueKey[0].split('-')[2]);
      } else if (Date.now() - startTime >= timeout) {
        reject(new Error(`Timeout: ${key} not found in sessionStorage within ${timeout}ms`));
      } else {
        setTimeout(checkKey, interval);
      }
    };

    checkKey();
  });
}

function waitForLocalStorageKey(key = 'alia-user-actions', timeout = 10000, interval = 50) {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    const checkKey = () => {
      const value = localStorage.getItem(key);
      if (value !== null && value !== undefined) {
        resolve(value);
      } else if (Date.now() - startTime >= timeout) {
        reject(new Error(`Timeout: ${key} not found in localStorage within ${timeout}ms`));
      } else {
        setTimeout(checkKey, interval);
      }
    };

    checkKey();
  });
}

function waitForSetVariationChoice(timeout = 10000, interval = 200) {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    const checkFunction = () => {
      if (
        typeof window.igData !== 'undefined' &&
        typeof window.igData?.user !== 'undefined' &&
        typeof window.igData?.user.assignTestGroup !== 'undefined'
      ) {
        resolve(window.igData?.user.assignTestGroup);
      } else if (Date.now() - startTime >= timeout) {
        reject(new Error('Timeout: setVariationChoice was not defined in time.'));
      } else {
        setTimeout(checkFunction, interval);
      }
    };

    checkFunction();
  });
}

// Popup -> Intelligems group map for the live test. offer: what the matched
// popup promises, applied to the page off-www so the content agrees with the
// popup on the very first view (Intelligems only re-applies its variation JS
// on subsequent page loads). Keep the variations symmetric — both write their
// code — or the test is biased. exclusiveOfferVariant indexes EXCLUSIVE_OFFERS
// below so www renders the matching experience.
const ALIA_POPUP_VARIATIONS = {
  A: {
    _fp: { testId: "2442b12e-2fa7-417b-a728-b7c347ced897", variationId: "58b533c0-67f6-40ea-a0e6-50c64284902a" },
    popupIds: [304030, 308872],
    offer: { body: window.lookT('look.offer.up_to_off_sitewide', { percent: 35 }), amount: window.lookT('look.offer.up_to_amount', { percent: 35 }), code: "NEWLOOK" },
    exclusiveOfferVariant: 0
  },
  B: {
    _fp: { testId: "2442b12e-2fa7-417b-a728-b7c347ced897", variationId: "47bbd29f-02af-4ff7-ac2e-eb6953ada558" },
    popupIds: [308845, 308873],
    offer: { body: window.lookT('look.offer.up_to_off_sitewide', { percent: 35 }), amount: window.lookT('look.offer.up_to_amount', { percent: 35 }), code: "NEWLOOK" },
    exclusiveOfferVariant: 0
  }
};

var setVariablesForFigPii = function (formIdRaw, formVariationIdRaw) {
  console.log('formId: ', formIdRaw);

  var formId = parseInt(formIdRaw);
  var formVariationId = parseInt(formVariationIdRaw);

  const matchedVariation = Object.values(ALIA_POPUP_VARIATIONS).find(variation =>
    variation.popupIds.indexOf(formId) !== -1
  );
  if (!matchedVariation) return;

  waitForSetVariationChoice().then(() => {
    console.log('setting FP variation ID: ', matchedVariation._fp.testId, matchedVariation._fp.variationId);
    window.igData?.user.assignTestGroup(matchedVariation._fp.testId, matchedVariation._fp.variationId);
    window._fpPopupVariationSet = true;
    document.documentElement.dataset.formId = formId;
    document.documentElement.dataset.formVariationId = formVariationId;
    // Off-www (try.lookoptic.com landing pages, review apps): swap the on-page
    // offer blocks to match the popup right now, and write the shared
    // welcome_offer_v4 cookie so www renders the matching experience on
    // click-through (see the activation resolver at the bottom of this file).
    if (window.location.hostname !== "www.lookoptic.com" && matchedVariation.offer && typeof window.utilWelcomeOffer === "function") {
      window.utilWelcomeOffer(matchedVariation.offer.body, matchedVariation.offer.amount, matchedVariation.offer.code, true);
    }
  }).catch((e) => {
    console.log('Intelligems not available to set variation choice.', e.message);
  });
};

waitForLocalStorageKey().then(
  (value) => {
    try {
      const aliaUserActions = JSON.parse(value);
      const today = new Date();
      const isToday = (dateString) => {
        const date = new Date(dateString);
        return date.getUTCFullYear() === today.getUTCFullYear() &&
               date.getUTCMonth() === today.getUTCMonth() &&
               date.getUTCDate() === today.getUTCDate();
      };
      const firstFlow = aliaUserActions.find(action => typeof action.flowID !== 'undefined' && isToday(action.createdAt));
      if (firstFlow) {
        setVariablesForFigPii(parseInt(firstFlow.flowID), parseInt(firstFlow.id || 0));
        console.log('caught popup via local storage', parseInt(firstFlow.flowID), parseInt(firstFlow.id || 0));
      }
    } catch (e) {
      console.log(e);
    }
  }
).catch((e) => {
  // Timeout is expected on pages where Alia stores nothing (e.g. landing pages).
  console.log(e.message);
});

waitForSessionStorageKey().then(
  (value) => {
    try {
      setVariablesForFigPii(parseInt(value), parseInt(value || 0));
      console.log('caught popup via session storage', parseInt(value), parseInt(value || 0));
    } catch (e) {
      console.log(e);
    }
  }
).catch((e) => {
  // Timeout is expected when no popup fires within the window.
  console.log(e.message);
});

document.addEventListener(
  'alia:popupView',
  (e) => {
    try {
      const campaignId = e.detail.popupID;
      setVariablesForFigPii(parseInt(campaignId), 0);
      console.log('caught popup via alia:popupView', parseInt(campaignId));
    } catch (e) {
      console.log(e);
    }
  }
);

document.addEventListener(
  'alia:signup',
  (e) => {
    try {
      const campaignId = e.detail.popupID;
      setVariablesForFigPii(parseInt(campaignId), 0);
      console.log('caught popup via alia:signup', parseInt(campaignId));
    } catch (e) {
      console.log(e);
    }
  }
);

document.addEventListener(
  'alia:campaignMatched',
  (e) => {
    try {
      const campaignId = e.detail.popupID;
      setVariablesForFigPii(parseInt(campaignId), 0);
      console.log('caught popup via alia:campaignMatched', parseInt(campaignId));
    } catch (e) {
      console.log(e);
    }
  }
);

const aliaRootObserver = new MutationObserver((mutations, obs) => {
  const target = document.querySelector('body > div[id^="alia-root-"]');
  if (target) {
    try {
      const popupId = parseInt(target.getAttribute('id').replace('alia-root-', ''));
      setVariablesForFigPii(popupId, 0);
      console.log('caught popup via mutation observer', popupId);
      obs.disconnect();
    } catch (e) {
      console.log(e);
    }
  }
});

setTimeout(() => {
  aliaRootObserver.disconnect();
  console.log('MutationObserver timeout - disconnected after 10 seconds');
}, 10000);

aliaRootObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
});

document.addEventListener("custom:alia:signup", (e) => {
  document.documentElement.classList.add('al_form_optin');
  // Dual-write: shared cookie (cross-subdomain) + localStorage (legacy readers).
  const alOptinDateSignup = new Date().toISOString();
  setSharedCookie('al_form_optin', 'true', 8);
  setSharedCookie('al_form_optin_date', alOptinDateSignup, 8);
  window.localStorage.setItem('al_form_optin', 'true');
  window.localStorage.setItem('al_form_optin_date', alOptinDateSignup);
  document.head.insertAdjacentHTML('beforeend', `<!-- Conversion Pixel for [signup]- DO NOT MODIFY --><img src="https://arttrk.com/pixel/?ad_log=referer&action=signup&pixid=3e9daba1-2d4b-40c6-8ef2-9177ac0d1547" width="1" height="1" border="0"><!-- End of Conversion Pixel -->`);
});

// Prefer the shared cookie, fall back to localStorage during rollout.
if ((getSharedCookie('al_form_optin') || window.localStorage.getItem('al_form_optin')) === 'true') {
  document.documentElement.classList.add('al_form_optin');
}

if (document.referrer.includes('try.lookoptic.com')) {
  // Dual-write: shared cookie (cross-subdomain) + localStorage (legacy readers).
  const alOptinDateReferrer = new Date().toISOString();
  setSharedCookie('al_form_optin', 'true', 8);
  setSharedCookie('al_form_optin_date', alOptinDateReferrer, 8);
  window.localStorage.setItem('al_form_optin', 'true');
  window.localStorage.setItem('al_form_optin_date', alOptinDateReferrer);
  document.documentElement.classList.add('al_form_optin');
}

if (window.location.search.includes('cm_flow_msg_id=campaign') && window.location.search.includes('SALE')) {
  removeSharedCookie('al_form_optin');
  window.localStorage.removeItem('al_form_optin');
}

const optinDate = getSharedCookie('al_form_optin_date') || window.localStorage.getItem('al_form_optin_date');
if (optinDate && (Date.now() - new Date(optinDate).getTime()) >= 8 * 24 * 60 * 60 * 1000) {
  removeSharedCookie('al_form_optin');
  window.localStorage.removeItem('al_form_optin');
}

// Discount codes that outrank the welcome offer: when one of these is the
// active discount_code, the cart promo bar and PDP offer stand down.
const CONFLICTING_DISCOUNT_CODES = ["WCRF26READERS", "VDAY", "love22", "LOVE22", "MEMORIAL", "JEFFFREEREADERS"];

const FREE_SHIPPING_HTML = window.lookT('look.offer.free_shipping_over_html');

const EXCLUSIVE_OFFERS = {
  0: {
    tiered: true,
    announcementHtml: window.lookT('look.offer.bf_early_access_announcement'),
    bannerMessage: window.lookT('look.offer.up_to_sitewide_msg', { percent: 35 }),
    bannerHtml: window.lookT('look.offer.bf_early_access_banner_html'),
    bannerHtmlMobile: window.lookT('look.offer.bf_early_access_banner_mobile_html'),
    bannerBackground: "#781313",
    tagLabel: window.lookT('look.offer.up_to_off', { percent: 35 }),
    bannerClosedKey: "lo-offer-banner-closed-bf-early-access-26",
    amount: window.lookT('look.offer.up_to_amount', { percent: 35 }),
    code: "BF26",
    offerBody: window.lookT('look.offer.up_to_off_sitewide', { percent: 35 }),
    congratsText: window.lookT('look.offer.congrats_saved_up_to', { percent: 35 }),
    // The Lens Type switch swaps products in place and rewrites the URL, so look
    // the tier up by the current handle and the extra by the selected variant on
    // every reprice.
    priceMultiplier: () => {
      const handle = (window.location.pathname.match(/\/products\/([^/]+)/) || [])[1];
      const variantId = document.querySelector('product-form input[name="id"]')?.value;
      let sale = {};
      try {
        sale = JSON.parse(document.getElementById("sale-tiers")?.textContent || "{}");
      } catch (e) {}
      const tier = sale.tiers?.[handle];
      return tier ? 1 - (tier + (sale.extra?.[variantId] || 0)) / 100 : null;
    }
  },
  1: {
    announcementHtml: window.lookT('look.offer.off_sitewide_code', { percent: 22, code: "NEWLOOK22" }),
    bannerMessage: window.lookT('look.offer.save_sitewide', { percent: 22 }),
    tagLabel: window.lookT('look.offer.off', { percent: 22 }),
    amount: "22%",
    code: "NEWLOOK22",
    offerBody: window.lookT('look.offer.off_sitewide', { percent: 22 }),
    congratsText: window.lookT('look.offer.congrats_saved', { percent: 22 }),
    priceMultiplier: () => 0.78
  },
  2: {
    announcementHtml: window.lookT('look.offer.up_to_off_sitewide_code', { percent: 20, code: 'LOOK20' }),
    bannerMessage: window.lookT('look.offer.save_up_to', { percent: 20 }),
    tagLabel: window.lookT('look.offer.up_to_off', { percent: 20 }),
    amount: "20%",
    code: "LOOK20",
    offerBody: window.lookT('look.offer.up_to_off_sitewide', { percent: 20 }),
    congratsText: window.lookT('look.offer.congrats_saved', { percent: 20 }),
    // "Up to": 20% on progressives/blue-light/sun/tint products, 15% elsewhere.
    priceMultiplier: () => {
      try {
        return window.location.pathname.split("/")[2].match(/(progressive|blue|sun|tint)/) ? 0.8 : 0.85;
      } catch (e) {
        return 0.85;
      }
    }
  }
};

function renderWelcomeOfferExperience(offer) {
  function hasConflictingDiscount(codes = CONFLICTING_DISCOUNT_CODES) {
    return codes.some(code => document.cookie.includes(`discount_code=${code}`));
  }

  function openOfferPopup({ requireMember = false } = {}) {
    if (requireMember && !["is_member", "is_email_subscribed", "is_sms_subscribed"].some(key => window.localStorage.getItem(key) === "true")) return;
    window.alia = window.alia || [];
    window.alia.push({ type: "open", campaignID: 17960, ignoreTargeting: true });
  }

  const isTiered = Boolean(offer.tiered);

  function offerMultiplier() {
    if (window.meta?.product?.type === "Gift Card") return null;
    return offer.priceMultiplier();
  }

  // The tiered offer's checkout price, as the super-app tiered-offer-discount
  // function computes it: a sale price whose dollar figure ends in 0 or 1 drops
  // to the 9 below, cents kept ($60.20 -> $59.20). Change both together.
  function offerSalePrice(regularPrice, multiplier) {
    const percent = Math.round((1 - multiplier) * 100);
    const cents = Math.round((Math.round(regularPrice * 100) * (100 - percent)) / 100);
    const dollars = Math.floor(cents / 100);
    const ones = dollars % 10;
    return (isTiered && dollars >= 10 && ones <= 1 ? cents - (ones + 1) * 100 : cents) / 100;
  }

  // Cart items that count toward the offer: lines with a sale tier for tiered
  // offers, otherwise everything except accessories and gift cards.
  function eligibleCartCount() {
    if (isTiered) return document.querySelectorAll("#cart-notification-product .cart-notification-product-item [data-sale-tier]").length;
    const bubble = document.querySelector("#cart-icon-bubble > div > span:nth-child(1)");
    let total = 0;
    if (bubble) total = parseInt(bubble.textContent.trim()) || 0;
    let accessories = 0;
    document.querySelectorAll("#cart-notification-product .cart-notification-product-item").forEach(item => {
      let productType = item.getAttribute("data-product-type");
      if (productType === null) {
        const typed = item.querySelector("[data-product-type]");
        productType = typed ? typed.getAttribute("data-product-type") : "";
      }
      const qtyEl = item.querySelector(".cart-notification__quantity");
      let qty = 1;
      if (qtyEl) {
        const parsed = parseInt(qtyEl.textContent.trim().replace("Qty: ", ""));
        if (!isNaN(parsed)) qty = parsed;
      }
      if (productType === "" || productType === "ACCESSORIES" || productType === "Gift Card") accessories += qty;
    });
    return total - accessories;
  }

  const bannerClosedKey = offer.bannerClosedKey || "figpii-just-for-you-closed-feb-26";

  function insertOfferBanner() {
    if (localStorage.getItem(bannerClosedKey) || document.querySelector(".figpii-just-for-you-wrapper")) return;
    const header = document.querySelector(".header-wrapper > .header");
    if (!header) return;
    const bannerHtml = `
                <div class="figpii-just-for-you-wrapper">
                    <a class="figpii-just-for-you-content" href="${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}collections/reader-products">
                        <p class="lo-offer-banner__text--desktop"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none">
    <path d="M8.61444 1.86333C8.81808 1.51396 9.18784 1.3 9.58708 1.3H9.64603C10.2382 1.3 10.7178 1.78479 10.7178 2.38333C10.7178 2.98187 10.2382 3.46667 9.64603 3.46667H7.68199L8.61444 1.86333ZM5.10436 1.86333L6.03681 3.46667H4.07277C3.48061 3.46667 3.00099 2.98187 3.00099 2.38333C3.00099 1.78479 3.48061 1.3 4.07277 1.3H4.13172C4.53095 1.3 4.9034 1.51396 5.10436 1.86333ZM7.50515 1.20521L6.8594 2.31562L6.21365 1.20521C5.77958 0.457708 4.98646 0 4.13172 0H4.07277C2.77055 0 1.71485 1.06708 1.71485 2.38333C1.71485 2.77333 1.80863 3.14167 1.97208 3.46667H0.857425C0.383162 3.46667 0 3.85396 0 4.33333V5.2C0 5.67938 0.383162 6.06667 0.857425 6.06667H12.8614C13.3356 6.06667 13.7188 5.67938 13.7188 5.2V4.33333C13.7188 3.85396 13.3356 3.46667 12.8614 3.46667H11.7467C11.9102 3.14167 12.0039 2.77333 12.0039 2.38333C12.0039 1.06708 10.9482 0 9.64603 0H9.58708C8.73233 0 7.93922 0.457708 7.50515 1.2025V1.20521ZM12.8614 7.36667H7.50247V13H11.1465C12.0924 13 12.8614 12.2227 12.8614 11.2667V7.36667ZM6.21633 7.36667H0.857425V11.2667C0.857425 12.2227 1.62643 13 2.57227 13H6.21633V7.36667Z" fill="white"/>
    </svg>${offer.bannerHtml || `<b>${window.lookT('look.offer.exclusive_offer')}</b> ${offer.bannerMessage}. ${window.lookT('look.offer.use_code')} ${offer.code}`}</p>
                        <p class="lo-offer-banner__text--mobile"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none">
    <path d="M8.61444 1.86333C8.81808 1.51396 9.18784 1.3 9.58708 1.3H9.64603C10.2382 1.3 10.7178 1.78479 10.7178 2.38333C10.7178 2.98187 10.2382 3.46667 9.64603 3.46667H7.68199L8.61444 1.86333ZM5.10436 1.86333L6.03681 3.46667H4.07277C3.48061 3.46667 3.00099 2.98187 3.00099 2.38333C3.00099 1.78479 3.48061 1.3 4.07277 1.3H4.13172C4.53095 1.3 4.9034 1.51396 5.10436 1.86333ZM7.50515 1.20521L6.8594 2.31562L6.21365 1.20521C5.77958 0.457708 4.98646 0 4.13172 0H4.07277C2.77055 0 1.71485 1.06708 1.71485 2.38333C1.71485 2.77333 1.80863 3.14167 1.97208 3.46667H0.857425C0.383162 3.46667 0 3.85396 0 4.33333V5.2C0 5.67938 0.383162 6.06667 0.857425 6.06667H12.8614C13.3356 6.06667 13.7188 5.67938 13.7188 5.2V4.33333C13.7188 3.85396 13.3356 3.46667 12.8614 3.46667H11.7467C11.9102 3.14167 12.0039 2.77333 12.0039 2.38333C12.0039 1.06708 10.9482 0 9.64603 0H9.58708C8.73233 0 7.93922 0.457708 7.50515 1.2025V1.20521ZM12.8614 7.36667H7.50247V13H11.1465C12.0924 13 12.8614 12.2227 12.8614 11.2667V7.36667ZM6.21633 7.36667H0.857425V11.2667C0.857425 12.2227 1.62643 13 2.57227 13H6.21633V7.36667Z" fill="white"/>
    </svg>${offer.bannerHtmlMobile || offer.bannerHtml || `${offer.bannerMessage} | ${window.lookT('look.offer.code_label')} ${offer.code}`}</p>
                    </a>
                    <div class="figpii-just-for-you-close">
                        <button class="figpii-just-for-you-close-button" type="button" aria-label="${window.lookT('look.offer.close_banner')}">
                            <span class="figpii-just-for-you-close-button-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                            </span>
                        </button>
                    </div>
                </div>
            `;
    // Desktop: below the header, unless the page opts in with [data-offer-banner-above]
    // (sections/bf-image-hero.liquid). Mobile: above it. The 768px switch must match
    // the .lo-offer-banner__text breakpoints in insertOfferStyles.
    const bannerAboveOnDesktop = Boolean(document.querySelector("[data-offer-banner-above]"));
    const placeBanner = (banner) => {
      if (window.innerWidth > 768 && !bannerAboveOnDesktop) {
        if (header.nextElementSibling !== banner) header.after(banner);
      } else if (header.previousElementSibling !== banner) {
        header.before(banner);
      }
    };
    header.insertAdjacentHTML("afterend", bannerHtml);
    placeBanner(header.nextElementSibling);
    window.addEventListener("resize", () => {
      const banner = document.querySelector(".figpii-just-for-you-wrapper");
      if (banner) placeBanner(banner);
    });
    document.body.addEventListener("click", (e) => {
      if (e.target.closest(".figpii-just-for-you-close-button")) {
        document.querySelector(".figpii-just-for-you-wrapper").remove();
        localStorage.setItem(bannerClosedKey, "true");
      }
    });
  }

  window.welcomeOfferActive = true;
  insertOfferStyles();
  insertOfferBanner();

  window._fpPLPCollectionTag = {
    _debounceTimer: null,
    _isUpdating: false,
    init: function () {
      this.addTag();
      const self = this;
      new MutationObserver(() => {
        if (self._isUpdating || document.querySelector(".grid__item .card__inner .fp_bfcm__plp-tag")) return;
        clearTimeout(self._debounceTimer);
        self._debounceTimer = setTimeout(() => self.addTag(), 150);
      }).observe(document.body, { childList: true, subtree: true });
    },
    addTag: function () {
      this._isUpdating = true;
      document.querySelectorAll(".grid__item .card-information-grid__reviews").forEach(reviews => {
        const item = reviews.closest(".grid__item");
        const card = item.querySelector(".card__inner");
        if (!card || card.querySelector(".fp_bfcm__plp-tag")) return;
        if (isTiered ? !card.hasAttribute("data-sale-tier") : item.querySelector('a[href*="-sale"]')) return;
        const tag = document.createElement("span");
        tag.className = "fp_bfcm__plp-tag";
        tag.textContent = offer.tagLabel;
        tag.setAttribute("role", "button");
        tag.setAttribute("tabindex", "0");
        tag.setAttribute("aria-label", offer.tagLabel + " — " + window.lookT('look.offer.see_details'));
        tag.addEventListener("click", () => openOfferPopup());
        tag.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openOfferPopup();
          }
        });
        card.appendChild(tag);
      });
      this._isUpdating = false;
    }
  };
  if (window.location.pathname.includes("/collections")) {
    window._utils.waitForElementToDisplay(".five-blocks-section, .footer__content-bottom", window._fpPLPCollectionTag.init.bind(window._fpPLPCollectionTag), 100, 1e4);
  }

  window._fpSitewideBanner = {
    init: function () {
      const desktop = document.querySelector(".swiper-wrapper--announcement-bar-desktop > .swiper-slide:nth-child(1) .announcement-bar__message");
      const mobile = document.querySelector(".swiper-wrapper--announcement-bar-mobile > .swiper-slide:nth-child(1) .announcement-bar__message");
      if (desktop) desktop.innerHTML = offer.announcementHtml;
      if (mobile) mobile.innerHTML = offer.announcementHtml;
    }
  };
  window._utils.waitForElementToDisplay(".swiper-wrapper--announcement-bar-desktop > .swiper-slide:nth-child(1) .announcement-bar__message", window._fpSitewideBanner.init, 100, 1e4);

  window.utilWelcomeOffer(offer.offerBody, offer.amount, offer.code, true);

  window._fpCartMessage = {
    _isUpdating: false,
    init: function () {
      const cartNotification = document.querySelector("cart-notification");
      document.querySelector("cart-notification .announcement-bar__message.h5").innerHTML = FREE_SHIPPING_HTML;
      const self = this;
      new MutationObserver(() => {
        if (self._isUpdating) return;
        const message = document.querySelector("cart-notification .announcement-bar__message.h5");
        if (message && !message.innerHTML.includes(window.lookT('look.offer.free_shipping_prefix'))) {
          self._isUpdating = true;
          message.innerHTML = FREE_SHIPPING_HTML;
          self._isUpdating = false;
        }
      }).observe(cartNotification, { childList: true, subtree: true });
    }
  };
  window._utils.waitForElementToDisplay("cart-notification", window._fpCartMessage.init, 100, 1e4);

  window._utils.waitForCondition(() => window.express_checkout, function () {
    window.fp_cart_bf_promotion = {
      promoBarInserted: false,
      observer: null,
      _debounceTimer: null,
      init: function () {
        if (hasConflictingDiscount(["WCRF26READERS", "VDAY"])) return;
        this.addPromotionBar();
        this.observeCart();
      },
      observeCart: function () {
        const cartNotification = document.querySelector("#cart-notification");
        if (!cartNotification) return;
        if (this.observer) {
          this.observer.observe(cartNotification, { childList: true, subtree: true });
          return;
        }
        const self = this;
        this.observer = new MutationObserver(() => {
          self.observer.disconnect();
          clearTimeout(self._debounceTimer);
          self._debounceTimer = setTimeout(() => {
            self.addPromotionBar();
            self.observeCart();
          }, 100);
        });
        this.observer.observe(cartNotification, { childList: true, subtree: true });
      },
      addPromotionBar: function () {
        if (this.observer) this.observer.disconnect();
        const eligibleCount = eligibleCartCount();
        if (eligibleCount < 1) {
          if (this.promoBarInserted) {
            const bar = document.querySelector("#fp-promo-bar");
            if (bar) bar.remove();
            this.promoBarInserted = false;
          }
          if (this.observer) this.observeCart();
          return;
        }
        const congratsText = offer.congratsText;
        const progressWidth = 100;
        let bar = document.querySelector("#fp-promo-bar");
        if (bar) {
          bar.querySelector(".congrats-span").innerHTML = congratsText;
          bar.querySelector(".progress-indicator").style.width = progressWidth + "%";
        } else {
          bar = document.createElement("div");
          bar.id = "fp-promo-bar";
          bar.classList.add("fp-congrats");
          const icon = document.createElement("img");
          icon.src = "https://cdn.shopify.com/s/files/1/0797/7215/files/red-gift-icon.png?v=1731561251";
          const content = document.createElement("div");
          content.className = "fp-promo-content";
          const congratsSpan = document.createElement("span");
          congratsSpan.className = "congrats-span";
          congratsSpan.innerHTML = congratsText;
          const progressBar = document.createElement("div");
          progressBar.className = "progress-bar";
          const progressIndicator = document.createElement("div");
          progressIndicator.className = "progress-indicator";
          progressIndicator.style.width = progressWidth + "%";
          progressBar.appendChild(progressIndicator);
          content.appendChild(congratsSpan);
          content.appendChild(progressBar);
          bar.appendChild(icon);
          bar.appendChild(content);
          const cartContents = document.querySelector("#cart-notification > div.cart-contents");
          if (cartContents) {
            cartContents.insertBefore(bar, cartContents.firstChild);
            this.promoBarInserted = true;
          }
        }
        if (this.observer) this.observeCart();
      }
    };
    if (!hasConflictingDiscount(["love22", "LOVE22"])) {
      window._utils.waitForCondition(() => document.querySelector("#cart-notification") && window.fp_bf_checkout_shipping, window.fp_cart_bf_promotion.init.bind(window.fp_cart_bf_promotion), 25, 1e4);
    }
  }, 25, 1e4);

  if (window.location.pathname.includes("/products/") && !document.getElementById("fp-slashed-pricing-styles")) {
    const slashedStyle = document.createElement("style");
    slashedStyle.id = "fp-slashed-pricing-styles";
    slashedStyle.textContent = `
      html.slashed-pricing .price.price--large:not(.price--sold-out) .price-item.price-item--regular {
          text-decoration: line-through !important;
          margin-right: 0.25rem !important;
      }

      html.slashed-pricing .price.price--large:not(.price--sold-out) > div:nth-child(1) {
          display: flex !important;
          flex-direction: row !important;
          width: 100% !important;
          flex-wrap: nowrap !important;
      }

      html.slashed-pricing .price.price--large:not(.price--sold-out) .price-item.price-item--sale {
          margin-right: 1rem !important;
      }

      html.slashed-pricing .product__title-price:has(.price.price--sold-out) .fp_bfcm {
          display: none;
      }
      `;
    document.head.appendChild(slashedStyle);

    window._utils.waitForElementToDisplay(".price-item.price-item--regular", function () {
      function addSlashedPricing() {
        const priceMultiplier = offerMultiplier();
        if (!priceMultiplier) return;
        const prices = document.querySelectorAll(".price.price--large");
        if (!prices.length) return;
        let anySlashed = false;
        prices.forEach(price => {
          const saleItem = price.querySelector(".price-item.price-item--sale.price-item--last");
          if (price.classList.contains("price--sold-out")) {
            if (saleItem) saleItem.closest(".price__sale").style.display = "none";
            return;
          }
          const regularItem = price.querySelector(".price-item.price-item--regular");
          if (!regularItem) return;
          // Intelligems price tests can change the base price — read it from
          // igData when available so the slash sits on the tested price.
          const meta = window.ShopifyAnalytics?.meta;
          const variantId = new URLSearchParams(window.location.search).get("variant") || meta?.selectedVariantId || meta?.product?.variants?.[0]?.id;
          const igPrice = variantId ? window.igData?.price?.getPriceByVariantId?.(variantId) || null : null;
          const regularPrice = igPrice ? parseFloat(igPrice) : parseFloat(regularItem.innerHTML.replace("$", ""));
          const regularLabel = `$${regularPrice.toFixed(2)}`;
          if (regularItem.innerHTML !== regularLabel) regularItem.innerHTML = regularLabel;
          if (saleItem) {
            const saleLabel = `$${offerSalePrice(regularPrice, priceMultiplier).toFixed(2)}`;
            if (saleItem.innerHTML !== saleLabel) saleItem.innerHTML = saleLabel;
            saleItem.closest(".price__sale").style.display = "block";
            anySlashed = true;
          }
        });
        document.documentElement.classList.toggle("slashed-pricing", anySlashed);
      }
      // Keep pricing errors contained — this callback runs synchronously inside
      // renderWelcomeOfferExperience, so a throw here took the rest of it down.
      const safeAddSlashedPricing = () => {
        try {
          addSlashedPricing();
        } catch (e) {
          console.warn("addSlashedPricing failed", e);
        }
      };
      safeAddSlashedPricing();
      let debounceTimer;
      const debouncedAdd = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(safeAddSlashedPricing, 10);
      };
      document.querySelectorAll(".price.price--large").forEach(price => {
        new MutationObserver(debouncedAdd).observe(price, { characterData: true, subtree: true, childList: true });
      });
    }, 100, 1e4);
  }

  function insertOfferStyles() {
    if (document.getElementById("fp-offer-styles")) return;
    const offerStyle = document.createElement("style");
    offerStyle.id = "fp-offer-styles";
    offerStyle.textContent = `
        .figpii-just-for-you-wrapper {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 8px 3.4rem;
            background-color: ${offer.bannerBackground || "rgb(177, 137, 32)"};
        }
        @media (max-width: 768px) {
            .figpii-just-for-you-wrapper {
                padding: 6px 3.4rem;
            }
        }
        .figpii-just-for-you-wrapper {
            position: relative;
        }
        .figpii-just-for-you-content {
            color: #fff;
            text-decoration: none;
        }
        /* Stretches the link over the whole bar; the close button sits above it. */
        .figpii-just-for-you-content::after {
            content: "";
            position: absolute;
            inset: 0;
        }
        .figpii-just-for-you-content svg {
            margin-right: 6px;
        }
        .figpii-just-for-you-content p {
            margin: 0;
            text-align: center;
        }
        @media (max-width: 768px) {
            .lo-offer-banner__text--desktop {
                display: none;
            }
        }
        @media (min-width: 769px) {
            .lo-offer-banner__text--mobile {
                display: none;
            }
        }
        .lo-offer-banner__rotate {
            display: inline-grid;
        }
        .lo-offer-banner__rotate > * {
            grid-area: 1 / 1;
            animation: lo-offer-banner-rotate 6s linear infinite;
        }
        .lo-offer-banner__rotate > * + * {
            animation-delay: -3s;
        }
        @keyframes lo-offer-banner-rotate {
            0%, 45% { opacity: 1; }
            50%, 95% { opacity: 0; }
            100% { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
            .lo-offer-banner__rotate > * {
                grid-area: auto;
                animation: none;
            }
        }
        .figpii-just-for-you-close {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            position: absolute;
            right: 3.4rem;
            z-index: 1;
        }
        @media (max-width: 768px) {
            .figpii-just-for-you-close {
                right: 6px;
            }
        }
        .figpii-just-for-you-close-button {
            background-color: transparent;
            border: none;
            cursor: pointer;
        }
        .figpii-just-for-you-close-button svg {
            stroke: #fff;
        }
        @media (max-width: 768px) {
            .figpii-just-for-you-close-button svg {
                width: 18px;
                height: 18px;
            }
        }
        body:has(.figpii-just-for-you-wrapper) #sticky-header .announcement-bar {
            display: none;
        }

        /* Matches the /collections/all-sale chip and takes the corner
           from the bestseller/new chip and collab logo (lo-card-tags). */
        body[data-page-type=collection] .card__inner .fp_bfcm__plp-tag {
            position: absolute;
            top: 10px;
            right: 10px;
            z-index: 2;
            cursor: pointer;
            background: #781313;
            color: #fff;
            padding: 3px 6px 2px;
            font-family: "Avenir Next", sans-serif;
            font-size: 12px;
            font-weight: 500;
            letter-spacing: 0.05em;
            line-height: 1.8;
            overflow: hidden;
        }
        body[data-page-type=collection] .card__inner .fp_bfcm__plp-tag::after {
            content: "";
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            width: 40%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.45), transparent);
            transform: translateX(-120%) skewX(-20deg);
            animation: fp-plp-tag-shine 3.2s ease-in-out infinite;
            pointer-events: none;
        }
        @keyframes fp-plp-tag-shine {
            0%, 70% { transform: translateX(-120%) skewX(-20deg); }
            100% { transform: translateX(260%) skewX(-20deg); }
        }
        @media (prefers-reduced-motion: reduce) {
            body[data-page-type=collection] .card__inner .fp_bfcm__plp-tag::after {
                display: none;
            }
        }
        body[data-page-type=collection] .card__inner:has(.fp_bfcm__plp-tag) :is(.lo-card-tag, .lo-collab-label) {
            display: none;
        }

        body[data-page-type=product]:not(.fp_bfcm--show) .fp_bfcm.tag {
            display: none !important;
        }
        body[data-page-type=product] .fp_bfcm--hidden {
            display: none !important;
        }
        body[data-page-type=product] .fp_bfcm.tag {
            position: absolute !important;
            right: 0 !important;
            top: 44px !important;
            z-index: 3 !important;
        }
        body[data-page-type=product] .fp_bfcm__tag {
            display: flex !important;
            background: #761818 !important;
            width: fit-content !important;
            padding: 5px 8px !important;
            border-radius: 50px !important;
            height: 24px !important;
            align-items: center !important;
            justify-content: center !important;
            margin-right: 0.5rem !important;
        }
        body[data-page-type=product] .fp_bfcm__tag p {
            color: #FFFFFF !important;
            font-weight: 600 !important;
            font-family: "Avenir Next" !important;
            font-size: 12px !important;
            font-style: normal !important;
            text-transform: uppercase !important;
            margin: 0 !important;
            padding: 0 !important;
            white-space: nowrap !important;
            line-height: 0 !important;
        }
        body[data-page-type=product] .fp_bfcm__tag p b {
            font-weight: 600 !important;
        }
        body[data-page-type=product] .fp_bfcm__info {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
        }
        body[data-page-type=product] #FacetFiltersForm .product-count.light {
            display: none !important;
        }
        body[data-page-type=product] #FacetFiltersForm.facets__form {
            gap: 0 !important;
        }
        @media screen and (min-width: 990px) {
            body[data-page-type=product] .product__title-price {
                position: relative !important;
            }
            body[data-page-type=product] .fp_bfcm.tag {
                position: absolute !important;
                right: 0 !important;
                bottom: -25px !important;
                z-index: 3 !important;
                top: unset !important;
            }
        }
        @media screen and (max-width: 1024px) {
            body[data-page-type=product] section > div > div.figpii.figpii__new-name__wrapper > div.figpii.figpii__new-name__review > div:nth-child(2) {
                max-width: calc(100vw - 180px) !important;
            }
            body[data-page-type=product] body .product__info-container .product__description {
                margin: 1.5rem 0 !important;
            }
            body[data-page-type=product] .fp_bfcm__tag p {
                font-size: 12px !important;
            }
            body[data-page-type=product] .fp_bfcm.tag {
                position: absolute !important;
                right: 20px !important;
                top: 64px !important;
                z-index: 3 !important;
            }
            body[data-page-type=product] div.figpii.figpii__new-name__wrapper > div.figpii.figpii__new-name__review {
                padding-right: 148px !important;
            }
        }
        @media screen and (max-width: 385px) {
            body[data-page-type=product] .fp_bfcm__tag p {
                font-size: 10px !important;
            }
        }

        .redeem-link {
            color: #FFFFFF !important;
        }
        #cart-notification div.announcement-bar > p {
            color: #781313 !important;
            font-weight: 500 !important;
            font-family: "Avenir Next", sans-serif !important;
        }

        body:has(#cart-notification-product .cart-notification-product-item .original-line-price) #fp-promo-bar {
            display: none;
        }
        #fp-promo-bar > img {
            display: none;
        }
        #fp-promo-bar {
            margin: 37px 20px 0px 20px;
            border: 1px solid rgb(239, 239, 239);
            display: flex;
            align-items: center;
            justify-content: space-around;
            padding: 10px;
            margin-bottom: -21px;
        }
        #fp-promo-bar > img {
            width: 22px;
            height: 22px;
            position: relative;
            top: 4px;
        }
        #fp-promo-bar > div {
            width: 95%;
            display: flex;
            flex-direction: column;
            gap: 5px;
        }
        #fp-promo-bar > div > span {
            font-family: "Avenir Next";
            font-size: 12px;
            font-weight: 500;
            line-height: 16.39px;
            letter-spacing: 0.04em;
            text-align: center;
            text-underline-position: from-font;
            text-decoration-skip-ink: none;
            color: rgba(70, 70, 70, 1);
        }
        #fp-promo-bar > div > div {
            height: 8px;
            background: rgba(239, 239, 239, 1);
            display: block !important;
        }
        #fp-promo-bar > div > div > div {
            height: 8px;
            background: rgba(118, 24, 24, 1);
            display: block !important;
        }
        #fp-promo-bar.fp-congrats {
            padding-bottom: 16px;
        }
        @media only screen and (max-width: 750px) {
            #fp-promo-bar > div {
                display: flex;
                flex-direction: column;
                gap: 5px;
            }
            #fp-promo-bar {
                margin-bottom: -25px;
                justify-content: center;
                position: relative;
            }
            #fp-promo-bar > img {
                width: 22px;
                height: 22px;
                position: absolute !important;
                left: 16px;
                bottom: 14px;
                right: unset;
                top: unset !important;
            }
            .cart-notification__heading {
                font-size: 18px !important;
            }
            .modal__close-button.link {
                width: 22px !important;
                height: 22px !important;
            }
            .modal__close-button .icon {
                width: 0.9rem !important;
                height: 0.9rem !important;
            }
        }

        @media screen and (max-width: 1024px) {
            body .product__info-container .product__description {
                margin: 1.5rem 0;
            }
            html #fp_upsell_slider_container {
                display: none !important;
            }
        }
    `;
    document.head.appendChild(offerStyle);
  }
}

window.executeExclusiveOffer = function (variant) {
  const offer = EXCLUSIVE_OFFERS[variant];
  if (!offer) return;
  if (!window.location.href.includes('www.lookoptic.com') || window.location.pathname.includes('-sale')) return;

  const run = () => {
    if (window.hasExecuteExclusiveOffer) return;
    window.hasExecuteExclusiveOffer = true;
    console.log('executeExclusiveOffer', variant);
    if (offer.code !== window.SITEWIDE_SALE_CODE) window.localStorage.setItem('ExclusiveOffer_v4', String(variant));
    renderWelcomeOfferExperience(offer);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
};

// Resolve which offer experience to render on www, in precedence order:
//   1. the shared welcome_offer_v4 cookie — matches the popup the visitor saw,
//      wherever they saw it (LP or www), so it always wins;
//   2. the ExclusiveOffer_v4 choice persisted from a previous visit;
//   3. a try.lookoptic.com referrer — the landing pages promise the offer even
//      before any popup fires, so honor it here — or a targeted ad UTM.
// No welcome_offer_v4 cookie and no referrer means no offer experience — and note
// this never assigns anyone to a test group; the popup listener above is the
// only assigner, so un-popped visitors stay out of the test analytics.
if (window.location.hostname === 'www.lookoptic.com' && !window.hasExecuteExclusiveOffer) {
  let offerVariant = window.SITEWIDE_SALE_CODE ? 0 : null;
  try {
    const storedOffer = getSharedCookie('welcome_offer_v4') || window.localStorage.getItem('welcome_offer_v4');
    if (offerVariant === null && storedOffer) {
      const parsedOffer = JSON.parse(storedOffer);
      if (parsedOffer && parsedOffer.amount) {
        offerVariant = 0;
      }
    }
  } catch (e) {
    console.log(e.message);
  }
  if (offerVariant === null && window.localStorage.getItem('ExclusiveOffer_v4')) {
    offerVariant = parseInt(window.localStorage.getItem('ExclusiveOffer_v4'));
  }
  if (offerVariant === null && document.referrer.includes('try.lookoptic.com')) {
    offerVariant = 0;
  }
  if (offerVariant === null && window.location.search.toLowerCase().includes('shopping')) {
    offerVariant = 0;
  }
  if (offerVariant === null && window.location.search.toLowerCase().includes('prospecting')) {
    offerVariant = 0;
  }
  if (offerVariant === null && window.location.search.toLowerCase().includes('acquisition')) {
    offerVariant = 0;
  }
  if (offerVariant === null && window.location.search.toLowerCase().includes('search%20-%20nb')) {
    offerVariant = 0;
  }
  if (offerVariant === null && window.location.search.toLowerCase().includes('search%20-%20nonbrand')) {
    offerVariant = 0;
  }
  if (offerVariant !== null && EXCLUSIVE_OFFERS[offerVariant]) {
    window.executeExclusiveOffer(offerVariant);
  }
}
